import {
  isUpstreamBlockedReasonCode,
  StepExecution,
  StepExecutionTerminalReasonContext,
} from '@/types/stepExecutions.ts';
import { ExecutionStatus } from '@/types/statuses.ts';
import { StepType } from '@/types/automationFields.ts';

type TerminalReasonRelation = 'external_upstream' | 'child_step' | 'child_scope';

export type TerminalReasonPresentation = {
  relation: TerminalReasonRelation | null;
  isBlockedByExternalUpstream: boolean;
  isProtectedScopeSkippedUpstream: boolean;
  upstreamBlockerCount: number;
  message: string | null;
};

export const getTerminalReasonPresentationFromParts = ({
  status,
  terminalReasonCode,
  terminalReasonContext,
  currentStepPath,
  stepType,
}: {
  status?: ExecutionStatus | null;
  terminalReasonCode?: string | null;
  terminalReasonContext?: StepExecutionTerminalReasonContext | null;
  currentStepPath?: string;
  stepType?: StepType | null;
}): TerminalReasonPresentation => {
  if (![
    ExecutionStatus.Errors,
    ExecutionStatus.PartiallyCompleted,
  ].includes(status as ExecutionStatus) || !isUpstreamBlockedReasonCode(terminalReasonCode)) {
    return {
      relation: null,
      isBlockedByExternalUpstream: false,
      isProtectedScopeSkippedUpstream: false,
      upstreamBlockerCount: 0,
      message: null,
    };
  }

  const relation = getTerminalReasonRelation(terminalReasonContext, currentStepPath);
  const isProtectedScopeSkippedUpstream = isFailureHandlerProtectedScopeSkippedUpstream({
    stepType,
    terminalReasonContext,
  });
  const upstreamBlockerCount = isProtectedScopeSkippedUpstream
    ? getUpstreamBlockerCount(terminalReasonContext)
    : 0;
  return {
    relation,
    isBlockedByExternalUpstream: relation === 'external_upstream',
    isProtectedScopeSkippedUpstream,
    upstreamBlockerCount,
    message: isProtectedScopeSkippedUpstream
      ? getProtectedScopeSkippedUpstreamMessage(
          terminalReasonCode,
          terminalReasonContext,
          upstreamBlockerCount,
        )
      : getTerminalReasonMessage({
          terminalReasonCode,
          terminalReasonContext,
          relation,
        }),
  };
};

export const getTerminalReasonPresentation = (
  stepExecution: StepExecution | undefined,
  currentStepPath?: string,
): TerminalReasonPresentation => {
  return getTerminalReasonPresentationFromParts({
    status: stepExecution?.status,
    terminalReasonCode: stepExecution?.terminal_reason_code,
    terminalReasonContext: stepExecution?.terminal_reason_context,
    currentStepPath,
    stepType: stepExecution?.step_type,
  });
};

const isFailureHandlerProtectedScopeSkippedUpstream = ({
  stepType,
  terminalReasonContext,
}: {
  stepType?: StepType | null;
  terminalReasonContext?: StepExecutionTerminalReasonContext | null;
}) => {
  return (
    stepType === StepType.FailureHandler &&
    terminalReasonContext?.reason_relation === 'external_upstream' &&
    terminalReasonContext?.protected_scope_outcome === 'upstream_skipped' &&
    terminalReasonContext?.protected_scope_started === false
  );
};

const getProtectedScopeSkippedUpstreamMessage = (
  terminalReasonCode: string | null | undefined,
  terminalReasonContext: StepExecutionTerminalReasonContext | null | undefined,
  upstreamBlockerCount: number,
) => {
  if (upstreamBlockerCount > 1) {
    return `Protected steps did not start because ${upstreamBlockerCount} upstream steps ended first. No response steps ran.`;
  }
  const upstreamStepName = getContextString(terminalReasonContext, 'upstream_step_name');
  const upstreamOutcome = getUpstreamOutcome(terminalReasonCode);
  return upstreamStepName
    ? `Protected steps did not start because upstream step ${upstreamStepName} ${upstreamOutcome}. No response steps ran.`
    : `Protected steps did not start because an upstream step ${upstreamOutcome}. No response steps ran.`;
};

const getUpstreamBlockerCount = (
  terminalReasonContext: StepExecutionTerminalReasonContext | null | undefined,
): number => {
  const dependencies = terminalReasonContext?.['upstream_terminal_dependencies'];
  return Array.isArray(dependencies) ? dependencies.length : 0;
};

const getUpstreamOutcome = (terminalReasonCode: string | null | undefined) => {
  switch (terminalReasonCode) {
    case 'upstream_completed_with_issues':
      return 'completed with issues';
    case 'upstream_cancelled':
      return 'was cancelled';
    case 'upstream_needs_input':
      return 'needs input';
    default:
      return 'failed';
  }
};

const getTerminalReasonRelation = (
  context: StepExecutionTerminalReasonContext | null | undefined,
  currentStepPath?: string,
): TerminalReasonRelation => {
  if (context?.reason_relation === 'child_step' || context?.reason_relation === 'child_scope') {
    return context.reason_relation;
  }
  if (context?.reason_relation === 'external_upstream') {
    return 'external_upstream';
  }

  const upstreamFullPath = getContextString(context, 'upstream_full_path');
  if (
    currentStepPath &&
    upstreamFullPath &&
    isSameOrDescendantPath(currentStepPath, upstreamFullPath)
  ) {
    return 'child_step';
  }

  return 'external_upstream';
};

const getTerminalReasonMessage = ({
  terminalReasonCode,
  terminalReasonContext,
  relation,
}: {
  terminalReasonCode?: string | null;
  terminalReasonContext?: StepExecutionTerminalReasonContext | null;
  relation: TerminalReasonRelation;
}) => {
  const upstreamStepName = getContextString(terminalReasonContext, 'upstream_step_name');
  const reasonSubjectStepName = getContextString(terminalReasonContext, 'reason_subject_step_name');
  const childScopeFailedCount = getContextNumber(
    terminalReasonContext,
    'reason_failed_child_count',
  );
  const childStepName = reasonSubjectStepName || upstreamStepName;

  switch (terminalReasonCode) {
    case 'upstream_failed':
      if (relation === 'child_step') {
        return childStepName
          ? `This step stopped because ${childStepName} could not complete.`
          : 'This step stopped because a step within this flow could not complete.';
      }
      if (relation === 'child_scope') {
        if (childScopeFailedCount && childScopeFailedCount > 1) {
          return `${childScopeFailedCount} parts of this flow could not complete.`;
        }
        return 'A part of this flow could not complete.';
      }
      return upstreamStepName
        ? `This step did not run because upstream step ${upstreamStepName} failed.`
        : 'This step did not run because an upstream step failed.';
    case 'upstream_completed_with_issues':
      if (relation === 'child_step') {
        return childStepName
          ? `This step stopped because ${childStepName} completed with issues.`
          : 'This step stopped because a step within this flow completed with issues.';
      }
      if (relation === 'child_scope') {
        if (childScopeFailedCount && childScopeFailedCount > 1) {
          return `${childScopeFailedCount} parts of this flow completed with issues.`;
        }
        return 'A part of this flow completed with issues.';
      }
      return upstreamStepName
        ? `This step did not run because upstream step ${upstreamStepName} completed with issues.`
        : 'This step did not run because an upstream step completed with issues.';
    default:
      return 'This step did not run because an upstream step stopped.';
  }
};

const getContextString = (
  context: StepExecutionTerminalReasonContext | null | undefined,
  key: keyof StepExecutionTerminalReasonContext,
) => {
  const value = context?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

const getContextNumber = (
  context: StepExecutionTerminalReasonContext | null | undefined,
  key: keyof StepExecutionTerminalReasonContext,
) => {
  const value = context?.[key];
  return typeof value === 'number' ? value : null;
};

const isSameOrDescendantPath = (parentPath: string, candidatePath: string) =>
  candidatePath === parentPath || candidatePath.startsWith(`${parentPath}.`);
